import { useEffect, useCallback, useMemo } from 'react'
import { getUniqueRecurrenceOptions, findRecurrenceOptionByKey } from '../utils/recurrenceOptionsHelper'
import { isWeeklyRecurrence } from 'utils/service'
import { adjustArrayToLength, ARRAY_DEFAULTS, isValidEventOccurrence, isValidDayOfTheWeek } from '../utils/arrayUtils'

/**
 * Custom hook to handle transitions between weekly and non-weekly recurrence intervals.
 *
 * This hook manages the complex logic when users switch between different recurrence types
 * (e.g., weekly vs monthly). When transitioning to non-weekly intervals, it ensures that
 * interval-specific form fields are reset to valid default values to prevent invalid
 * combinations that could cause form submission errors.
 *
 * @param {boolean} open - Whether the dialog is currently open (only runs when true)
 * @param {string|Object} serviceInterval - The current service interval selection
 * @param {number} currentFrequency - The current frequency value for array sizing
 * @param {Object} formData - The current form data containing interval-specific fields
 * @param {Array|*} formData.eventOccurrence - Week of month occurrence values
 * @param {Array|*} formData.dayOfTheWeek - Day of week values
 * @param {Function} setValue - React Hook Form setValue function to update form fields
 * @param {Object} metaFiltersData - Metadata containing available recurrence options
 *
 * @example
 * // Usage in component:
 * useIntervalTransition(open, formData.serviceInterval, currentFrequency, formData, setValue, metaFiltersData)
 *
 * // Transition scenario:
 * // User switches from "Weekly" to "Monthly" recurrence
 * // Hook detects non-weekly interval and resets:
 * // - eventOccurrence to ["First", "First", ...] (valid month weeks)
 * // - dayOfTheWeek to ["Sunday", "Sunday", ...] (valid week days)
 *
 * @note This hook prevents form submission errors by ensuring data consistency
 * when switching between incompatible recurrence interval types.
 */
export const useIntervalTransition = (open, serviceInterval, currentFrequency, formData, setValue, metaFiltersData) => {
  // Memoize recurrence options to prevent unnecessary recalculations
  const recurrenceOptions = useMemo(() => {
    return getUniqueRecurrenceOptions(metaFiltersData)
  }, [metaFiltersData])

  // Memoize weekly status check to prevent unnecessary recalculations
  const isCurrentlyWeekly = useMemo(() => {
    if (!serviceInterval) return true // Default to weekly if no interval

    const serviceIntervalObj = findRecurrenceOptionByKey(recurrenceOptions, serviceInterval)
    return serviceIntervalObj ? isWeeklyRecurrence(serviceIntervalObj.recurrenceInterval) : true
  }, [serviceInterval, recurrenceOptions])

  // Memoize the transition logic
  const handleIntervalTransition = useCallback(() => {
    if (!open || isCurrentlyWeekly || currentFrequency <= 0) return

    const { eventOccurrence, dayOfTheWeek } = formData
    const updates = {}

    // Reset eventOccurrence if it contains invalid values for non-weekly intervals
    if (!Array.isArray(eventOccurrence) || eventOccurrence.some(val => !isValidEventOccurrence(val))) {
      updates.eventOccurrence = adjustArrayToLength(
        eventOccurrence,
        currentFrequency,
        ARRAY_DEFAULTS.eventOccurrence,
        isValidEventOccurrence
      )
    }

    // Reset dayOfTheWeek if it contains invalid values for non-weekly intervals
    if (!Array.isArray(dayOfTheWeek) || dayOfTheWeek.some(val => !isValidDayOfTheWeek(val))) {
      updates.dayOfTheWeek = adjustArrayToLength(dayOfTheWeek, currentFrequency, ARRAY_DEFAULTS.dayOfTheWeek, isValidDayOfTheWeek)
    }

    // Apply all updates at once to minimize re-renders
    Object.entries(updates).forEach(([field, value]) => {
      setValue(field, value)
    })
  }, [open, isCurrentlyWeekly, currentFrequency, formData, setValue])

  useEffect(() => {
    handleIntervalTransition()
  }, [handleIntervalTransition])
}
